import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { errorToast, infoToast } from 'configuration/notifications';
import { getUnixTimestamp } from 'helpers/utils';
import { agreementApi } from 'helpers/api/agreement';
import { clientApi } from 'helpers/api/client';

import { Stock } from 'store/stock/actions';
import { Client } from 'store/client/actions';
import { Rent } from 'store/transferAct/actions';
import { rentSelector, reserveSelector, rentInitalSelector } from 'store/transferAct/selectors';
import { stockListSelector, stockSelector } from 'store/stock/selectors';
import { downloadFiles } from 'store/cashWarrant/actions';
import { agreementsListSelector, organizationsListSelector } from 'store/client/selectors';
import { userDataSelector } from 'store/auth/selectors';

import { Header } from 'components/Header';
import { ButtonIcon, ButtonLabel } from 'components/Button';
import { Loader } from 'components/Loader';
import { Icon } from 'components/Icon';
import { HistoryPopup } from 'components/HistoryPopup';
import { ModalAttention } from 'components/ModalAttention';

import { InfoCardsSection } from './components/InfoCardsSection';
import { ActionButtonsSection } from './components/ActionButtonsSection';
import { RentTable } from './components/RentTable';

import './style.scss';
import { transferApi } from 'helpers/api/transfer';

const defaultRent = {
  instruments: [],
  client: null,
  agr: null,
  bonusSpent: 0,
  bonusAccured: 0,
  startDateTime: 0,
  endDateTime: 0,
  comment: '',
  fine: [],
  discount: [],
  address: '',
};

const RentPage = ({ isEdit, isExit }) => {
  const { rentId } = useParams();
  let state;
  if (rentId && isExit) {
    state = 'exit';
  }
  if (rentId && isEdit) {
    state = 'edit';
  } else if (rentId && !isEdit) {
    state = 'view';
  } else {
    state = 'create';
  }

  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const rent = useSelector(rentSelector);
  const reserve = useSelector(reserveSelector);
  const stock = useSelector(stockSelector);
  const stockList = useSelector(stockListSelector);
  const initalRent = useSelector(rentInitalSelector);
  const clientAgreements = useSelector(agreementsListSelector);
  const organizationList = useSelector(organizationsListSelector);
  const userData = useSelector(userDataSelector);

  const defaultOrg = organizationList.find((org) => org.orgID1C === userData.defaultOrgID);

  const [historyModal, setHistoryModal] = useState(null);
  const [pending, setPending] = useState(false);
  const [attentionModal, setAttentionModal] = useState(false);
  const [deletitionModal, setDeletitionModal] = useState(false);

  const [reload, setReload] = useState(0);
  const [isFullPledge, setIsFullPledge] = useState(false);

  useEffect(() => {
    if (location?.state?.fromMain) {
      updateRent({ ...defaultRent, instruments: location.state.instruments });
      dispatch(Stock.setStock(location.state.stock));
      return;
    } else if (location?.state?.fromClient) {
      if (location.state.selected) {
        dispatch(Client.getAgreements(location.state.selected.id));
        if (rent === null) {
          updateRent({ ...defaultRent, client: location.state.selected });
        } else {
          updateRent({
            client: location.state.selected,
          });
        }

        if (location.state.selected && location.state.selected.isTemporary) {
          infoToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
        }
      }
    } else if (!location?.state?.fromCheckout) {
      if (state === 'create') {
        const emptyRent = { ...defaultRent };

        if (location?.state?.fromReserve && reserve) {
          emptyRent.client = reserve.client;
          emptyRent.instruments = reserve.instruments;
          dispatch(Client.getAgreements(reserve.client.id));

          if (reserve.client && reserve.client.isTemporary) {
            infoToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
          }

          Stock.setStock(reserve.stock);
        } else if (!stock) {
          history.push('/');
        }

        dispatch(Rent.saveRent(emptyRent));
      } else {
        dispatch(Rent.getRent(rentId));
      }
    }
  }, [reload]);

  useEffect(() => {
    if (stockList === null) {
      dispatch(Stock.getStockList());
      return;
    }
    if (rent?.actID1C) {
      const findStock = stockList.find((item) => item.stockID1C === rent.stockID);

      if (!findStock) {
        errorToast('У Вас нет прав для редактирования этого склада!');
        history.push('/');
        return;
      }

      if (stock?.stockID1C !== findStock?.stockID1C) {
        dispatch(Stock.setStock(findStock));
      }
    }
  }, [rent, stockList]);

  const itemsSum = useCallback(
    (withPledge = false) => {
      const rateVAT = rent?.agr?.organization?.rateVAT || 0;

      if (!rent?.instruments) {
        return 0;
      }

      /// Считаем общую сумму инструментов
      return (
        rent.fine.reduce((acc, n) => acc + Number(n.sum), 0) -
        rent.discount.reduce((acc, n) => acc + Number(n.sum), 0) +
        rent.instruments.reduce((acc, cur) => {
          if (withPledge) {
            return (
              acc +
              Number(cur.pledge) * cur.quantity +
              (cur.itemRentSum * cur.quantity + ((cur.itemRentSum * cur.quantity) / 100) * rateVAT)
            );
          }

          return acc + (cur.itemRentSum * cur.quantity + ((cur.itemRentSum * cur.quantity) / 100) * rateVAT);
        }, 0)
      );
    },
    [rent]
  );

  useEffect(() => {
    const sum = itemsSum();
    const newRent = {};
    let canChanged = false;

    /// Пересчёт время окончания аренды и бонусов после пересчёта общей суммы
    if (rent?.instruments && state === 'create') {
      if (!isNaN(Number(sum))) {
        canChanged = true;
        newRent.bonusAccured = sum / 10;
      }

      const maxTime = rent.instruments.reduce((acc, cur) => {
        return acc < cur.rentTerm ? cur.rentTerm : acc;
      }, 0);

      canChanged = true;

      if (maxTime === 0) {
        newRent.endDateTime = 0;
      } else {
        newRent.endDateTime = (rent.startDateTime || getUnixTimestamp()) + maxTime * 3600 * 24;
      }
    }

    if (canChanged) {
      updateRent(newRent);
    }
  }, [itemsSum()]);

  const updateRent = (data) => {
    dispatch(Rent.updateRent(data));
  };

  const handleSetInRent = async () => {
    if (!rentId || pending) {
      return;
    }
    setPending(true);

    await dispatch(
      Rent.updateTransfer(rentId, {
        status: 'В аренде',
        isReadyFor1C: true,
        conduct: false,
      })
    );
    setPending(false);
  };

  const handleChangeStatus = async (newStatus) => {
    if (!rentId || pending) {
      return;
    }
    setPending(true);

    await dispatch(
      Rent.updateTransfer(rentId, {
        status: newStatus,
      })
    );
    setPending(false);
  };

  const handleCloseDeletitionModal = () => {
    if (rentId) {
      transferApi.deleteTransferAct(rentId);
      setDeletitionModal(false);
      history.push('/');
    }
  };

  const handleClose = (isSubmit) => {
    if (!isSubmit && JSON.stringify(initalRent) !== JSON.stringify(rent)) {
      setAttentionModal(true);
      return;
    }

    toggleAttentionModal(false);

    if (state === 'edit') {
      if (isSubmit) {
        setReload(reload + 1);
      }

      history.push(`/rent/${rentId}`);
    } else {
      history.push('/');
    }
  };

  const toggleAttentionModal = (toggle) => {
    setAttentionModal(toggle);
  };

  const handleDefaultAgreement = () => {
    agreementApi
      .createAgreement({
        clientID: rent.client.id,
        orgID: defaultOrg.orgID1C,
        date: getUnixTimestamp(),
        term: getUnixTimestamp() + 3600 * 24 * 365 * 2, // +2 года
        type: '',
      })
      .then(({ data } = {}) => {
        dispatch(Rent.updateRent({ agr: data }));
      });
  };

  const handleSave = async () => {
    const client = rent.client && (await clientApi.getClient(rent.client?.id));

    if (!rent.client) {
      return errorToast('Необходимо выбрать клиента');
    }
    if (rent.instruments.length === 0) {
      return errorToast('Необходимо добавить хотя бы один инструмент!');
    }
    if (!rent.agr) {
      if ((!clientAgreements || !clientAgreements.length) && defaultOrg) {
        handleDefaultAgreement();
      } else {
        return errorToast('Выберите договор с ФОПом');
      }
    }
    if (client.isTemporary && !isFullPledge) {
      return errorToast('Клиент не полностью заполнен. Отправка данных в 1C невозможна');
    }

    if (state === 'create') {
      history.push('/rent/checkout');
    } else {
      history.push(`/rent/${rentId}/checkout`);
    }
  };

  const handleOpenDeletitionModal = () => {
    setDeletitionModal(!deletitionModal);
  };

  const handleEdit = () => {
    if (rentId) {
      history.push(`/rent/${rentId}/edit`);
    }
  };

  const handleInfo = () => {
    if (historyModal) {
      return;
    }

    setHistoryModal(rent && rent.history);
  };

  const handleClickOutside = () => {
    if (historyModal) {
      setHistoryModal(null);
    }
  };

  const handleUpdateTable = (instrumentId, newData) => {
    const findIndex = rent.instruments.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      const newArray = rent.instruments.slice();

      newArray[findIndex] = newData;
      updateRent({ instruments: newArray });
    }
  };

  const handleRemoveTable = (instrumentId) => {
    const findIndex = rent.instruments.findIndex((inst) => inst.id === instrumentId);

    if (findIndex !== -1) {
      const newArray = rent.instruments.slice();
      newArray.splice(findIndex, 1);

      updateRent({ instruments: newArray });
    }
  };

  const handleExitRent = () => {
    if (rentId) {
      dispatch(Rent.clearRent());
      history.push(`/rent/${rentId}/exit`);
    }
  };

  const handleExtenseRent = () => {
    if (rentId) {
      dispatch(Rent.clearRent());
      history.push(`/rent/${rentId}/continue`);
    }
  };

  const handleDownload = async () => {
    infoToast('Загрузка файлов начнется через несколько секунд...');
    await downloadFiles(rent.actID1C);
  };

  if (!rent) {
    return <Loader />;
  }

  const labelList = [
    { status: 'Ожидает обработки', classColor: 'column-button--gr' },
    { status: 'Ожидает выдачи инструмента', classColor: 'column-button--bl' },
    { status: 'В аренде', classColor: 'column-button--ye' },
    { status: 'Истекает срок', classColor: 'column-button--or' },
    { status: 'Завершено', classColor: 'column-button--gr' },
  ];

  const oneOf = labelList.find((label) => rent.status === label.status);
  const labelColor = oneOf?.classColor || 'column-button--gr';

  return (
    <>
      <Header
        title={
          state === 'create' ? 'Новая аренда' : `Аренда до ${moment(rent.endDateTime * 1000).format('HH:mm DD.MM')}`
        }
        onClose={() => handleClose(false)}
      >
        {rent.actID1C && (
          <>
            <ButtonLabel className={labelColor} text={rent.status} />
            {rent.conduct && (
              <div style={{ marginLeft: 10 }} className="c1-button">
                1C
              </div>
            )}
          </>
        )}
      </Header>

      <main className="rent-main" onClick={handleClickOutside}>
        {state !== 'view' && <ActionButtonsSection />}
        <RentTable
          isExit={isExit}
          rateVAT={rent?.agr?.organization?.rateVAT || 0}
          canChange={state !== 'view'}
          instrumentsList={rent.instruments}
          onUpdate={handleUpdateTable}
          onRemove={handleRemoveTable}
          isFullPledge={isFullPledge}
          setIsFullPledge={setIsFullPledge}
        />

        <InfoCardsSection
          rent={rent}
          stock={stock}
          showEmpty={state !== 'view'}
          canDelete={state === 'edit' || state === 'create'}
        />

        <div className="rent-maintext">
          Общая сумма: <b>{itemsSum(true)} грн.</b>
        </div>

        <div className="buttons-block-grid">
          <div style={{ display: 'flex' }}>
            {state === 'view' && rent?.status === 'Ожидает выдачи инструмента' && (
              <ButtonIcon
                className="btn-main"
                onClick={() => handleChangeStatus('Ожидает обработки')}
                icon="arrow-left"
                text="Вернуть в статус “Ожидает обработки”"
              />
            )}
          </div>

          <div className="flex-center">
            {state === 'create' && (
              <ButtonIcon className="btn-main btn-save" onClick={handleSave} icon="save" text="Продолжить" />
            )}

            {state === 'view' && (
              <>
                <div className="btn-main" onClick={handleInfo}>
                  <Icon name="view-info" />
                  <HistoryPopup data={historyModal} onOutside={handleClickOutside} />
                </div>

                {rent && rent.status === 'Ожидает обработки' && (
                  <>
                    <ButtonIcon className="btn-main btn-save" onClick={handleEdit} icon="edit" text="Редактировать" />
                    <ButtonIcon className="btn-main" onClick={handleOpenDeletitionModal} icon="trash" text="Удалить" />

                    <ButtonIcon
                      className="btn-main btn-save"
                      onClick={handleSave}
                      icon="driller"
                      text={stock?.isSingleWorker ? 'Передать в аренду' : 'Передать на склад'}
                    />
                  </>
                )}

                {rent && rent.status === 'Ожидает выдачи инструмента' && (
                  <ButtonIcon
                    className="btn-main btn-save"
                    onClick={handleSetInRent}
                    icon="driller"
                    text="Инструмент выдан"
                  />
                )}
                {rent && ['В аренде', 'Истекает срок', 'Долг'].includes(rent.status) && (
                  <>
                    <ButtonIcon
                      className="btn-main btn-save"
                      icon="rent-continue"
                      onClick={handleExtenseRent}
                      text="Продлить аренду"
                    />
                    <ButtonIcon
                      className="btn-main btn-save"
                      icon="rent-exit"
                      onClick={handleExitRent}
                      text="Закрыть аренду"
                    />
                  </>
                )}
              </>
            )}

            {state === 'edit' && (
              <ButtonIcon className="btn-main btn-save" onClick={handleSave} icon="save" text="Продолжить" />
            )}

            {rent.status && rent.status !== 'Ожидает обработки' && rent.status !== 'Завершено' && (
              <div className="btn-main" onClick={handleDownload}>
                <Icon name="printer" />
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonIcon className="btn-main" icon="coin" text="Кассовые ордера" onClick={handleSave} />
          </div>
        </div>
      </main>

      {attentionModal && (
        <ModalAttention onClose={() => toggleAttentionModal(false)} onSubmit={() => handleClose(true)} />
      )}
      {deletitionModal && (
        <ModalAttention
          onClose={() => setDeletitionModal(false)}
          onSubmit={() => handleCloseDeletitionModal()}
          headerText={'Удаление аренды'}
          bodyText={'Вы точно хотите удалить аренду? Это действие безвозвратно'}
          submitTextButton={'Да, удалить'}
          cancelTextButton={'Нет, отмена'}
        />
      )}
    </>
  );
};

RentPage.propTypes = {
  isEdit: PropTypes.bool,
  isExit: PropTypes.bool,
};

export { RentPage };
